import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  FileText,
  Send,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Copy,
  Trash2,
  Eye,
  Loader2,
  Receipt, Printer} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { EscrowBadge } from '../components/PaymentStatusPanel';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { useTranslation } from '../i18n';

// Status colors
const statusColors = {
  draft: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-purple-100 text-purple-700',
  partially_paid: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
};

// Invoice Card
function InvoiceCard({ invoice, onView, onSend }) {
  const isPaid = invoice.status === 'paid';
  const isOverdue = invoice.status === 'overdue' ||
    (invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'paid');

  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(invoice)}
      data-testid={`invoice-card-${invoice._id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-mono text-sm font-medium">{invoice.invoice_number}</p>
            <p className="text-sm text-muted-foreground">{invoice.client_name}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <Badge className={statusColors[isOverdue ? 'overdue' : invoice.status]}>
              {isOverdue && invoice.status !== 'paid' ? 'Overdue' : invoice.status.replace('_', ' ')}
            </Badge>
            {invoice.deal_id && (
              <EscrowBadge status={invoice.escrow_status || 'pending'} />
            )}
          </div>
        </div>

        <div className="flex items-end justify-between">
          <div>
            <p className="text-2xl font-bold">
              {invoice.currency} {(invoice.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
            {invoice.paid_amount > 0 && invoice.paid_amount < invoice.total && (
              <p className="text-xs text-muted-foreground">
                Paid: {invoice.currency} {invoice.paid_amount.toLocaleString()}
              </p>
            )}
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <p>Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
          </div>
        </div>

        {/* {invoice.status === 'draft' && (
          <Button
            size="sm"
            className="w-full mt-3"
            onClick={(e) => {
              e.stopPropagation();
              onSend(invoice._id);
            }}
          >
            <Send className="h-3 w-3 mr-1" />
            Send Invoice
          </Button>
        )} */}
      </CardContent>
    </Card>
  );
}

// Create Invoice Dialog
function CreateInvoiceDialog({
  open, onOpenChange, onSuccess, clients = [], deals = [] }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_email: '',
    deal_id: '',
    currency: 'USD',
    notes: '',
    terms: 'Payment due within 30 days of invoice date.',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, tax_rate: 0, discount: 0 }
  ]);

  const createInvoice = useMutation({
    mutationFn: (data) => api.post('/invoices', data),
    onSuccess: (data) => {
      toast.success('Invoice created');
      onSuccess?.(data);
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
  });

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, tax_rate: 0, discount: 0 }]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    lineItems.forEach(item => {
      const lineSubtotal = item.quantity * item.unit_price;
      const discount = lineSubtotal * (item.discount / 100);
      const tax = (lineSubtotal - discount) * (item.tax_rate / 100);

      subtotal += lineSubtotal;
      totalDiscount += discount;
      totalTax += tax;
    });

    return {
      subtotal: subtotal.toFixed(2),
      discount: totalDiscount.toFixed(2),
      tax: totalTax.toFixed(2),
      total: (subtotal - totalDiscount + totalTax).toFixed(2)
    };
  };

  const totals = calculateTotals();

  const handleSubmit = () => {
    if (lineItems.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Please fill in all line items');
      return;
    }

    // Get client info
    let clientName = formData.client_name;
    let clientEmail = formData.client_email;

    if (formData.client_id) {
      const client = clients.find(c => c._id === formData.client_id);
      if (client) {
        clientName = clientName || client.name;
        clientEmail = clientEmail || client.email;
      }
    }

    createInvoice.mutate({
      ...formData,
      client_name: clientName,
      client_email: clientEmail,
      line_items: lineItems.map(item => ({
        ...item,
        quantity: parseFloat(item.quantity),
        unit_price: parseFloat(item.unit_price),
        tax_rate: parseFloat(item.tax_rate || 0),
        discount: parseFloat(item.discount || 0)
      }))
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("invoices.createInvoice")}</DialogTitle>
          <CardDescription>{t("invoices.createANewInvoice")}</CardDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Client Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("invoices.selectClient")}</Label>
              <Select
                value={formData.client_id}
                onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select existing client or enter new" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client._id} value={client._id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("invoices.dueDate")}</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>

          {!formData.client_id && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("invoices.clientName")}</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Client name"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("invoices.clientEmail")}</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                  placeholder="client@example.com"
                />
              </div>
            </div>
          )}

          {/* Line Items */}
          <div className="space-y-2">
            <Label>{t("invoices.lineItems")}</Label>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">{t("invoices.description")}</TableHead>
                    <TableHead className="w-[15%]">{t("invoices.qty")}</TableHead>
                    <TableHead className="w-[15%]">{t("invoices.price")}</TableHead>
                    <TableHead className="w-[10%]">{t("invoices.tax")}</TableHead>
                    <TableHead className="w-[15%]">{t("invoices.total")}</TableHead>
                    <TableHead className="w-[5%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Input
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Item description"
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.tax_rate}
                          onChange={(e) => updateLineItem(index, 'tax_rate', e.target.value)}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        ${(item.quantity * item.unit_price * (1 + item.tax_rate / 100)).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {lineItems.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button variant="outline" size="sm" onClick={addLineItem}>
              <Plus className="h-3 w-3 mr-1" />
              Add Line
            </Button>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("invoices.subtotal")}</span>
                <span>${totals.subtotal}</span>
              </div>
              {parseFloat(totals.discount) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t("invoices.discount")}</span>
                  <span>-${totals.discount}</span>
                </div>
              )}
              {parseFloat(totals.tax) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("invoices.tax1")}</span>
                  <span>${totals.tax}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-1">
                <span>{t("invoices.total1")}</span>
                <span>${totals.total}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>{t("invoices.notesOptional")}</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes for the client..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createInvoice.isPending}>
            {createInvoice.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Create Invoice
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Invoices Page
export default function InvoicesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [tabFilter, setTabFilter] = useState('all'); // all, my, received

  // Detail Modal State
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Get invoices
  

  // Export invoice as PDF
  const handleExportPdf = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API}/api/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('PDF export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export PDF');
    }
  };

  // Print current invoice
  const handlePrintInvoice = () => {
    window.print();
  };

  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.get('/invoices')
  });

  // Get stats
  const { data: stats } = useQuery({
    queryKey: ['invoice-stats'],
    queryFn: () => api.get('/invoices/stats')
  });

  // Get clients for the create dialog
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-invoice'],
    queryFn: () => api.get('/clients')
  });

  // Send invoice mutation
  const sendInvoice = useMutation({
    mutationFn: (id) => api.post(`/invoices/${id}/send`),
    onSuccess: () => {
      toast.success('Invoice sent');
      queryClient.invalidateQueries(['invoices']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to send invoice');
    }
  });

  // ESCROW MUTATIONS
  const fundEscrowMutation = useMutation({
    mutationFn: (invoiceId) => api.post(`/payments/checkout/${invoiceId}`),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe Checkout
      } else {
        toast.error('Failed to initiate secure checkout process');
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to initialize checkout');
    }
  });

  const releaseMutation = useMutation({
    mutationFn: (invoiceId) => api.post(`/payments/release/${invoiceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices']);
      queryClient.invalidateQueries(['invoice-stats']);
      toast.success('Escrow funds released to payee');
      setDetailModalOpen(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to release funds');
    }
  });

  const requestReleaseMutation = useMutation({
    mutationFn: (invoiceId) => api.post(`/payments/request-release/${invoiceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices']);
      toast.success('Sent release request to the payer');
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to request release');
    }
  });

  const disputeMutation = useMutation({
    mutationFn: ({ invoiceId, reason }) => api.post(`/payments/dispute/${invoiceId}`, { reason }),
    onSuccess: (data, { invoiceId, reason }) => {
      // Instantly update the open modal so it re-renders without waiting for refetch
      setSelectedInvoice((prev) => prev && String(prev._id) === String(invoiceId)
        ? {
          ...prev,
          escrow_status: 'disputed',
          dispute_count: (prev.dispute_count || 0) + 1,
          dispute_history: [
            ...(prev.dispute_history || []),
            { type: 'disputed', reason, timestamp: new Date().toISOString() }
          ]
        }
        : prev
      );
      // Refetch list in background (exact:false catches all statusFilter variants)
      queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
      toast.success('Dispute raised successfully. Funds frozen.');
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to raise dispute');
    }
  });

  const resolveDisputeMutation = useMutation({
    mutationFn: ({ invoiceId, reason }) => api.post(`/payments/dispute/${invoiceId}/resolve`, { reason }),
    onSuccess: (data, { invoiceId, reason }) => {
      // Instantly update the open modal
      setSelectedInvoice((prev) => prev && String(prev._id) === String(invoiceId)
        ? {
          ...prev,
          escrow_status: 'funded',
          dispute_history: [
            ...(prev.dispute_history || []),
            { type: 'resolved', reason, timestamp: new Date().toISOString() }
          ]
        }
        : prev
      );
      // Refetch list in background
      queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
      toast.success('Dispute resolved. Funds returned to escrow status.');
      setDetailModalOpen(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to resolve dispute');
    }
  });

  const withdrawMutation = useMutation({
    mutationFn: (invoiceId) => api.post(`/payments/withdraw/${invoiceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices']);
      toast.success('Withdrawal initiated! Funds are transferring to your connected bank.');
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to process withdrawal');
    }
  });

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailModalOpen(true);
  };

  const handleSend = (id) => {
    sendInvoice.mutate(id);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries(['invoices']);
    queryClient.invalidateQueries(['invoice-stats']);
  };

  const invoices = invoicesData?.invoices || [];
  const clients = clientsData || [];

  const filteredInvoices = invoices.filter(invoice => {
    if (tabFilter === 'all') return true;
    const isMine = invoice.user_id === user?._id || (user?.organization_id && invoice.organization_id === user.organization_id);
    if (tabFilter === 'my') return isMine;
    if (tabFilter === 'received') return !isMine;
    return true;
  });

  return (
    <div className="space-y-4 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-display font-bold">{t("invoices.invoices")}</h1>
          <p className="text-sm text-muted-foreground">{t("invoices.manageAndTrackYour")}</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-invoice-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Invoice
        </Button>
      </div>



      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold">
                ${(stats.total_invoiced || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">{t("invoices.totalInvoiced")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold text-green-600">
                ${(stats.total_paid || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">{t("invoices.paid")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold text-amber-600">
                ${(stats.total_outstanding || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">{t("invoices.outstanding")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-2xl font-bold">{stats.invoice_count || 0}</p>
              <p className="text-xs text-muted-foreground">{t("invoices.totalInvoices")}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tabFilter} onValueChange={setTabFilter} className="w-full">
        <TabsList>
          <TabsTrigger value="all">{t("invoices.allInvoices")}</TabsTrigger>
          <TabsTrigger value="my">{t("invoices.myInvoices")}</TabsTrigger>
          <TabsTrigger value="received">{t("invoices.receivedInvoices")}</TabsTrigger>
        </TabsList>
      </Tabs>



      {/* Invoice Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">{t("invoices.noInvoicesYet")}</h3>
            <p className="text-muted-foreground mb-4">
              Create your first invoice to get started
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvoices.map((invoice) => (
            <InvoiceCard
              key={invoice._id}
              invoice={invoice}
              onView={handleView}
              onSend={handleSend}
            />
          ))}
        </div>
      )}

      {/* Create Invoice Dialog */}
      <CreateInvoiceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleSuccess}
        clients={clients}
      />

      {/* Invoice Detail View (Phase 9) */}
      <InvoiceDetailModal
        open={detailModalOpen}
        onOpenChange={(isOpen) => {
          setDetailModalOpen(isOpen);
          if (!isOpen) setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        clients={clients}
        onSend={handleSend}
        fundEscrowMutation={fundEscrowMutation}
        releaseMutation={releaseMutation}
        requestReleaseMutation={requestReleaseMutation}
        disputeMutation={disputeMutation}
        resolveDisputeMutation={resolveDisputeMutation}
        withdrawMutation={withdrawMutation}
      />
    </div>
  );
}
